import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { AiFillCloseCircle } from '@react-icons/all-files/ai/AiFillCloseCircle';
import { bookHistory, key_bookUrl } from '../util/constants';
import {
  getUrlContext,
  initiate_interval_tree,
} from '../util/interval_tree.ts';
import { getServerBookUrlFromUrl } from '../util/util';
import { auth, db } from '../config/firebase';
import { Link, useNavigate } from 'react-router-dom';
import { deleteDoc, doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { collection, query, onSnapshot } from 'firebase/firestore';
import { useSelector } from 'react-redux';
import { Alert, Snackbar } from '@mui/material';

const ChevronRightIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-4 w-4 text-gray-400"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 5l7 7-7 7"
    />
  </svg>
);

// **HIGHLIGHT: New helper function to format all-caps text.**
const formatText = (text) => {
  if (!text || typeof text !== 'string') {
    return text;
  }

  if (true) {
    // This regex checks if a word consists only of Roman numeral characters.
    const romanNumeralRegex = /^[IVXLCDM\-—\.]+$/i;

    return text
      .toLowerCase()
      .split(' ')
      .map((word) => {
        // If the word matches the Roman numeral pattern, keep it uppercase.
        if (romanNumeralRegex.test(word)) {
          return word.toUpperCase();
        }
        // Otherwise, convert to Title Case.
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(' ');
  }
  return text;
};

function BookMarks(props) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const [bookMarksArr, setBookMarksArr] = useState([]);
  const [sortBy, setSortBy] = useState('date');
  // **HIGHLIGHT: State for managing the import process**
  const [importStatus, setImportStatus] = useState('');
  const fileInputRef = useRef(null); // Ref for the hidden file input

  initiate_interval_tree();
  const user = useSelector((state) => state.userReducer);

  // ... (Existing handlers like handleClose, handleDownload, etc.)
  const handleClick = () => {
    setOpen(true);
  };
  const handleClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setOpen(false);
  };

  const handleDownload = () => {
    if (bookMarksArr.length === 0) {
      alert('There are no bookmarks to download.');
      return;
    }

    // Create a new array with specific fields excluded from each bookmark.
    const bookmarksForDownload = bookMarksArr.map((bookmark) => {
      const { uid, created, shared, ...rest } = bookmark;
      return rest; // This object contains all fields EXCEPT the ones destructured out.
    });

    const jsonString = `data:text/json;charset=utf-8,${encodeURIComponent(
      JSON.stringify(bookmarksForDownload, null, 2)
    )}`;

    const link = document.createElement('a');
    link.href = jsonString;
    link.download = 'my-bookmarks.baws';
    link.click();
  };

  // **HIGHLIGHT: This function handles the file selection and import logic.**
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.baws')) {
      setImportStatus('Error: Invalid file type. Please select a .baws file.');
      // Reset the input value so the user can select the same file again if they wish
      event.target.value = null;
      return;
    }

    setImportStatus('Importing...');
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const jsonString = e.target.result;
        const bookmarksToImport = JSON.parse(jsonString);

        if (!Array.isArray(bookmarksToImport)) {
          throw new Error('JSON file must contain an array of bookmarks.');
        }

        let successCount = 0;
        const importPromises = bookmarksToImport.map(async (entry) => {
          // Validate required fields
          if (
            !entry ||
            typeof entry.url !== 'string' ||
            typeof entry.text !== 'string' ||
            typeof entry.pageNo !== 'number'
          ) {
            console.warn('Skipping invalid bookmark entry:', entry);
            return; // Skip this item
          }

          // Create a new bookmark object, excluding id/uid from the source file
          const newBookmark = {
            id: crypto.randomUUID(), // Generate a new unique ID
            url: entry.url,
            text: entry.text,
            pageNo: entry.pageNo,
            context: entry.context || [],
            title: entry.title || 'Untitled',
            baseUrl: entry.baseUrl || '',
            rects: entry.rects && Array.isArray(entry.rects) ? entry.rects : [],
            shared: false,
          };

          await addBookmark(newBookmark);
          successCount++;
        });

        await Promise.all(importPromises);
        setImportStatus(
          `Successfully imported ${successCount} of ${bookmarksToImport.length} bookmarks.`
        );
      } catch (error) {
        setImportStatus(`Error: ${error.message}`);
        console.error('Import failed:', error);
      }
    };

    reader.readAsText(file);
  };

  // **HIGHLIGHT: Helper function to add a single bookmark to Firestore.**
  const addBookmark = async (bookmarkData) => {
    const uid = auth.currentUser?.uid;
    if (!uid) throw new Error('User not authenticated.');

    const { id, ...dataToStore } = bookmarkData;
    if (!id) throw new Error("Bookmark data must have a unique 'id' property.");

    // Use the path structure `BookMarks/{userId}/collection/{bookmarkId}`
    const docRef = doc(db, 'BookMarks', uid, 'collection', id);
    dataToStore.uid = uid;
    dataToStore.created = serverTimestamp(); // Use server-side timestamp
    await setDoc(docRef, dataToStore);
  };

  useEffect(() => {
    const uid = auth?.currentUser?.uid;
    if (!uid) {
      setBookMarksArr([]);
      return;
    }
    const bookMarksRef = collection(db, `BookMarks/${uid}/collection`);
    const q = query(bookMarksRef);
    const unsubscribe = onSnapshot(q, (snapShot) => {
      const data = snapShot.docs.map((item) => ({
        ...item.data(),
        id: item.id,
        created: item.data().created?.toDate(),
      }));
      setBookMarksArr(data);
    });
    return () => unsubscribe();
  }, [user.isLoggedIn]);

  const processedBookmarks = useMemo(() => {
    const validBookmarks = bookMarksArr.filter((bm) => bm.created); // Filter out any items without a valid 'created' date
    if (sortBy === 'date') {
      return validBookmarks.sort((a, b) => b.created - a.created);
    }
    if (sortBy === 'book') {
      // ... (sorting and grouping logic remains the same)
      return validBookmarks.reduce((acc, bookmark) => {
        const pageContext =
          getUrlContext(new URL(`https://${bookmark.url}`).pathname) || [];
        const groupKey =
          pageContext.map((c) => formatText(JSON.parse(c).label)).join(' > ') ||
          'Uncategorized';
        if (!acc[groupKey]) acc[groupKey] = [];
        acc[groupKey].push(bookmark);
        return acc;
      }, {});
    }
    return [];
  }, [bookMarksArr, sortBy]);

  // ... (other functions like setBookContextUrl, removeBookMark, etc. remain the same)
  function setBookContextUrl(e, url) {
    e.preventDefault();
    bookHistory.set(key_bookUrl, {
      shortUrl: e.target.href,
      longUrl: getServerBookUrlFromUrl(url),
    });
    navigate(new URL(url).pathname);
  }

  const removeBookMark = async (id) => {
    try {
      await deleteDoc(
        doc(db, `BookMarks/${auth.currentUser.uid}/collection`, id)
      );
      handleClick();
    } catch (err) {
      console.log('Error', err);
    }
  };

  const generateUrl = (url, page) =>
    `https://${url.replace(/\d+$/, '')}${page}`;

  const renderBookmarkCard = (bookMarkObj) => {
    // ... (This function's JSX remains the same as the previous responsive version)
    const path_url = `https://${bookMarkObj.url}`;
    const pageContext = getUrlContext(new URL(path_url).pathname) || [];
    return (
      <div
        key={bookMarkObj.id}
        className="bg-white relative border-b md:border-b-0 md:rounded-lg md:shadow-md md:mb-4 md:border-l-4 md:border-blue-500 transition-shadow md:hover:shadow-lg"
      >
        <button
          onClick={() => removeBookMark(bookMarkObj.id)}
          className="absolute top-2 right-2 text-gray-400 hover:text-red-500 transition-colors z-10"
          aria-label="Remove bookmark"
        >
          <AiFillCloseCircle className="w-6 h-6" />
        </button>
        <div className="p-3 md:p-5">
          {bookMarkObj.text && (
            <blockquote className="mb-3 text-gray-700 md:border-l-4 md:border-gray-200 md:pl-4 md:italic">
              {bookMarkObj.text}
            </blockquote>
          )}
          <div className="text-sm text-gray-800">
            <a
              href={path_url}
              onClick={(e) => setBookContextUrl(e, path_url)}
              className="font-semibold text-blue-600 hover:underline"
            >
              On Page: {bookMarkObj.pageNo}
            </a>
          </div>
          {sortBy === 'date' && (
            <div className="text-xs text-gray-500 mt-2 flex items-center flex-wrap">
              {pageContext.map((context_str, index) => {
                const context = JSON.parse(context_str);
                const url = generateUrl(bookMarkObj.url, context.page_start);
                return (
                  <React.Fragment key={index}>
                    {' '}
                    <a
                      href={url}
                      onClick={(e) => setBookContextUrl(e, url)}
                      className="hover:underline"
                    >
                      {context.label}
                    </a>{' '}
                    {index < pageContext.length - 1 && <ChevronRightIcon />}{' '}
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (!user.isLoggedIn) {
    return (
      <div className="text-center p-8">
        <Link to="/login" className="text-blue-600 hover:underline">
          Please login to see your bookmarks.
        </Link>
      </div>
    );
  }

  return (
    <div className="p-2 md:p-6 lg:p-8 max-w-4xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 md:mb-6 gap-4">
        <div className="w-full sm:w-auto">
          <label
            htmlFor="sort-select"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Sort By
          </label>
          <Form.Select
            id="sort-select"
            aria-label="Sort by"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="w-full sm:w-48"
          >
            <option value="date">Date</option>
            <option value="book">Book</option>
          </Form.Select>
        </div>
        {/* **HIGHLIGHT: Buttons for import and download are grouped here.** */}
        <div className="flex items-center gap-2 self-end sm:self-center">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            accept=".baws"
            className="hidden"
          />
          <Button
            variant="secondary"
            onClick={() => fileInputRef.current.click()}
          >
            Import from JSON
          </Button>
          <Button variant="outline-primary" onClick={handleDownload}>
            Download BookMarks
          </Button>
        </div>
      </div>
      {/* **HIGHLIGHT: UI to show the import status message.** */}
      {importStatus && (
        <Alert
          severity={importStatus.startsWith('Error') ? 'error' : 'info'}
          className="mb-4"
        >
          {importStatus}
        </Alert>
      )}

      <div>
        {sortBy === 'date' && (
          <div className="border-t md:border-t-0">
            {processedBookmarks.map(renderBookmarkCard)}
          </div>
        )}
        {sortBy === 'book' &&
          Object.entries(processedBookmarks).map(([groupTitle, bookmarks]) => (
            <div key={groupTitle} className="mb-8">
              <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-3 pb-2 border-b-2 border-gray-200">
                {groupTitle}
              </h3>
              <div className="border-t md:border-t-0">
                {bookmarks.map(renderBookmarkCard)}
              </div>
            </div>
          ))}
      </div>

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
      >
        <Alert onClose={handleClose} severity="success" sx={{ width: '100%' }}>
          Bookmark Deleted Successfully
        </Alert>
      </Snackbar>
    </div>
  );
}
export default BookMarks;
